import { useState, useEffect, useCallback } from 'react';
import useProject from './useProject';

/**
 * Custom hook to manage project tasks
 * 
 * @param {Object} params - Parameters for the hook
 * @param {string} params.projectId - The project ID
 * @param {Object} [params.initialProject] - Initial project data (optional)
 * @returns {Object} Project tasks hook results
 */
const useProjectTasks = ({ projectId, initialProject }) => {
  const [localProject, setLocalProject] = useState(initialProject);
  const { 
    updateProject, 
    isUpdatingProject, 
    getProject, 
    project: queryProject,
    toggleTaskStatus,
    isTogglingTaskStatus
  } = useProject({ projectId });

  // Effect to fetch project data on component mount, projectId change, and after task selection
  useEffect(() => {
    if (projectId) {
      console.log('Fetching project data for ID', projectId);
      getProject(projectId)
        .then(fetchedProject => {
          if (fetchedProject) {
            console.log('Updated project from API:', fetchedProject);
            setLocalProject(fetchedProject);
          }
        })
        .catch(error => {
          console.error('Error fetching project:', error);
        });
    }
  }, [projectId, getProject]);

  // Add listener for custom event after task selection is saved
  useEffect(() => {
    const handleTaskSelectionCompleted = () => {
      console.log('Task selection completed event detected, refreshing project data');
      if (projectId) {
        getProject(projectId)
          .then(fetchedProject => {
            if (fetchedProject) {
              console.log('Refreshed project after task selection:', fetchedProject);
              setLocalProject(fetchedProject);
            }
          })
          .catch(error => {
            console.error('Error refreshing project:', error);
          });
      }
    };
    
    // Listen for custom event
    window.addEventListener('taskSelectionCompleted', handleTaskSelectionCompleted);
    
    // Clean up
    return () => {
      window.removeEventListener('taskSelectionCompleted', handleTaskSelectionCompleted);
    };
  }, [projectId, getProject]);

  // Update local project when initial project changes
  useEffect(() => {
    if (initialProject && (!localProject || initialProject._id !== localProject._id)) {
      setLocalProject(initialProject);
    }
  }, [initialProject, localProject]);

  // Update local project when query result changes
  useEffect(() => {
    if (queryProject) {
      setLocalProject(queryProject);
    }
  }, [queryProject]);

  // Refresh project data manually
  const refreshProject = useCallback(async () => {
    if (projectId) {
      try {
        const freshProjectData = await getProject(projectId);
        if (freshProjectData) {
          setLocalProject(freshProjectData);
          return freshProjectData;
        }
      } catch (error) {
        console.error('Error refreshing project:', error);
        throw error;
      }
    }
    return null;
  }, [projectId, getProject]);

  // Use the most up-to-date project data available
  const project = localProject || queryProject || initialProject;

  return {
    project,
    setLocalProject,
    refreshProject,
    updateProject,
    isUpdatingProject,
    toggleTaskStatus,
    isTogglingTaskStatus
  };
};

export default useProjectTasks;